import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import type { components } from "@/types/api.gen"

import type { QueueFilterParams } from "../queue-query-params"

/*
   `GET /api/v1/threads/summary` — the four tiles, counted by the backend over
   the whole filtered queue rather than the page on screen.

   It takes the same filters as `threadsQuery`, built by the same
   `buildThreadsParams`, so narrowing the list narrows the tiles with it — the
   legacy queue's `fetchThreadCounts` does the same, through one builder.
*/

export type ThreadCounts = components["schemas"]["ThreadCountsOut"]

export interface ThreadsSummaryParams {
  search: string
  /** Includes `include_archived`, so the tiles and the list agree on archived too. */
  filters: QueueFilterParams
}

/** The whole queue as the backend defines it by default: no search, no filter, archived out. */
export const UNFILTERED_SUMMARY: ThreadsSummaryParams = {
  search: "",
  filters: {},
}

function fetchThreadsSummary(
  params: ThreadsSummaryParams,
  signal: AbortSignal,
): Promise<ThreadCounts> {
  const search = params.search.trim()
  return http.get<ThreadCounts>("/api/v1/threads/summary", {
    params: {
      ...(search ? { search } : {}),
      ...params.filters,
    },
    signal,
  })
}

export const threadsSummaryQuery = (params: ThreadsSummaryParams) =>
  queryOptions({
    queryKey: qk.threads.summary(params),
    queryFn: ({ signal }) => fetchThreadsSummary(params, signal),
    // While a narrowed count is in flight, keep showing the last one rather than a skeleton.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
